import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Page as Page$1 } from 'react-pdf';
import PagePlaceholder from './PagePlaceholder.js';
import { usePDFContent } from './PDF.js';
import { styled } from 'styled-components';
import useDeferred from '@fabric-app/hooks/useDeferred';
import { useSubDocumentSearch } from '../../../../search/context/SubDocumentSearchContext.js';
import { RenderIfVisible } from '@fabric-app/ui/components/RenderIfVisible/RenderIfVisible';
import escape from '../../../../../node_modules/lodash-es/escape.js';

const Wrapper = styled.div.withConfig({
  displayName: "RowPage__Wrapper",
  componentId: "sc-719e5a4-0"
})([
  `position:relative;z-index:2;`
]);
const Page = styled(Page$1).withConfig({
  displayName: "RowPage__Page",
  componentId: "sc-719e5a4-1"
})([
  `background:none !important;&[data-resizing='true']{display:none;}z-index:2;span[data-nonmarked],mark{position:static;color:inherit;padding:0;margin:0;border-radius:2px;display:inline;line-height:inherit;font-size:inherit;font-weight:inherit;font-family:inherit;font-style:inherit;text-align:inherit;white-space:inherit;box-decoration-break:inherit;-webkit-box-decoration-break:inherit;box-shadow:none;background:none;opacity:0.25;}mark{opacity:0.35;position:relative;background-color:#fffb9e;box-shadow:0 0 0 2px #fffb9e,inset 0 0 0 1000px #fffb9e;}`,
  ``
], ({ activePositions }) => {
  if (!activePositions) return "";
  try {
    const positions = JSON.parse(activePositions);
    return positions.map((pos) => `
        mark[data-item="${pos.itemIndex}"][data-range="${pos.rangeIndex}"] {
          background-color: #fd0;
          box-shadow:
            0 0 0 2px #fd0,
            inset 0 0 0 1000px #fd0;
        }
      `).join("\n");
  } catch (_e) {
    return "";
  }
});
const createNonMarkedSpan = (text) => `<span data-nonmarked>${escape(text)}</span>`;
const createMark = (text, pageNumber, itemIndex, rangeIndex) => `<mark data-page="${pageNumber}" data-item="${itemIndex}" data-range="${rangeIndex}">${text}</mark>`;
const highlightWithRegex = (str, searchRegex, pageNumber, itemIndex, rangeIndex) => {
  let result = "";
  let lastIndex = 0;
  const regexWithGlobal = new RegExp(searchRegex.source, searchRegex.flags.includes("g") ? searchRegex.flags : searchRegex.flags + "g");
  let match;
  while ((match = regexWithGlobal.exec(str)) !== null) {
    if (match.index > lastIndex) {
      result += createNonMarkedSpan(str.substring(lastIndex, match.index));
    }
    result += createMark(escape(match[0]), pageNumber, itemIndex, rangeIndex);
    lastIndex = match.index + match[0].length;
    if (match.index === regexWithGlobal.lastIndex) {
      regexWithGlobal.lastIndex++;
    }
  }
  if (lastIndex < str.length) {
    result += createNonMarkedSpan(str.substring(lastIndex));
  }
  return result || createNonMarkedSpan(str);
};
const RowPage = ({ pageNumber, width, style, root }) => {
  const [ref, setRef] = useState(null);
  const { getEstimatedHeight, currentPage, scale, updateCurrentPage, textItemMatchData, activeMatch } = usePDFContent();
  const activePositionsString = useMemo(() => {
    if (!activeMatch) return "";
    const positions = activeMatch.positions.filter((pos) => pos.pageNumber === pageNumber).map((pos) => ({
      itemIndex: pos.itemIndex,
      rangeIndex: pos.rangeIndex
    }));
    return positions.length > 0 ? JSON.stringify(positions) : "";
  }, [
    activeMatch,
    pageNumber
  ]);
  const roundedScale = useMemo(() => Math.floor(scale * 100) / 100, [
    scale
  ]);
  const deferredScale = useDeferred(roundedScale);
  const height = useMemo(() => getEstimatedHeight(pageNumber, width, true), [
    getEstimatedHeight,
    pageNumber,
    width
  ]);
  const visibleOffset = useMemo(() => roundedScale > 1 ? height * 2 : height * 3, [
    roundedScale,
    height
  ]);
  const initialVisible = useMemo(() => roundedScale > 1 ? pageNumber < 2 : pageNumber < 4, [
    roundedScale,
    pageNumber
  ]);
  const [isResizing, setIsResizing] = useState(false);
  const debounce = useRef(null);
  useEffect(() => {
    setIsResizing(true);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      setIsResizing(false);
    }, 300);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [
    width
  ]);
  useEffect(() => {
    if (!ref) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        updateCurrentPage();
      }
    }, {
      threshold: 0.1,
      root
    });
    observer.observe(ref);
    return () => {
      observer.disconnect();
    };
  }, [
    ref,
    updateCurrentPage,
    root
  ]);
  const { searchRegex } = useSubDocumentSearch();
  const customTextRenderer = useCallback(({ str, itemIndex }) => {
    if (!searchRegex.source || searchRegex.source === "(?:)") {
      return createNonMarkedSpan(str);
    }
    const matchData = textItemMatchData[pageNumber]?.[itemIndex];
    if (!matchData) {
      return highlightWithRegex(str, searchRegex, pageNumber, itemIndex, 0);
    }
    if (matchData.ranges && matchData.ranges.length > 0) {
      const parts = [];
      let lastIndex = 0;
      matchData.ranges.forEach(([start, end], rangeIndex) => {
        if (start > lastIndex) {
          parts.push(createNonMarkedSpan(str.substring(lastIndex, start)));
        }
        parts.push(createMark(escape(str.substring(start, end)), pageNumber, itemIndex, rangeIndex));
        lastIndex = end;
      });
      if (lastIndex < str.length) {
        parts.push(createNonMarkedSpan(str.substring(lastIndex)));
      }
      return parts.join("");
    }
    return createNonMarkedSpan(str);
  }, [
    searchRegex,
    textItemMatchData,
    pageNumber
  ]);
  return /* @__PURE__ */ jsx(Fragment, {
    children: /* @__PURE__ */ jsx("div", {
      ref: setRef,
      "data-page-index": pageNumber,
      style: {
        position: "relative",
        width,
        height,
        willChange: "width, height",
        backgroundColor: "white",
        overflow: "hidden"
      },
      children: /* @__PURE__ */ jsxs(RenderIfVisible, {
        visibleOffset,
        root,
        rootElement: "div",
        defaultHeight: height,
        placeholderElement: "div",
        initialVisible,
        stayRendered: pageNumber === currentPage,
        children: [
          /* @__PURE__ */ jsx(PagePlaceholder, {
            pageNumber,
            absolute: true
          }),
          /* @__PURE__ */ jsx(Wrapper, {
            style: {
              ...style,
              width,
              height,
              willChange: "width, height",
              transform: `scale(${1 / deferredScale})`,
              transformOrigin: "0 0"
            },
            "data-row-page": true,
            children: !isResizing && /* @__PURE__ */ jsx(Page, {
              renderMode: "canvas",
              pageNumber,
              width,
              height,
              scale: deferredScale,
              loading: /* @__PURE__ */ jsx(Fragment, {}),
              customTextRenderer,
              activePositions: activePositionsString
            }, `${pageNumber}-page`)
          })
        ]
      }, pageNumber)
    }, pageNumber)
  });
};

export { RowPage as default };
//# sourceMappingURL=RowPage.js.map
